import { memo, useState, useCallback, useMemo, useEffect } from 'react';
import type { ReactNode } from 'react';
import {
  Box,
  Typography,
  Paper,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import type {
  Board,
  Hit,
  LedgerSummary,
  SquareWithNumbers,
  HighlightedCellsWithQuarter,
  NeighborDirection,
} from '../../types';
import {
  getNumbersForQuarter,
  getNumberSetKeyForQuarter,
  getNeighborPositions,
  getBoxNeighborPositions,
  CARDINAL_DIRECTIONS,
  ALL_NEIGHBOR_DIRECTIONS,
} from '../../types';
import { getQuarterLabels } from './HitsPanel';

export interface MySquaresPanelProps {
  board: Board;
  grid: SquareWithNumbers[][];
  hits: Hit[];
  currentUserId: string;
  summary?: LedgerSummary | null;
  onHighlight: (data: HighlightedCellsWithQuarter | null) => void;
}

interface SquareNumbersForQuarter {
  square: SquareWithNumbers;
  homeNumber: number;
  awayNumber: number;
}

interface NeighborInfo {
  count: number;
  amount: number;
  total: number;
  groupedWith: Array<{ row: number; col: number }>;
}

type RowType = 'rightWay' | 'wrongWay' | 'plusNRightWay' | 'plusNWrongWay' | 'neighborsRightWay' | 'neighborsWrongWay';

interface RowConfig {
  type: RowType;
  label: string;
  visible: boolean;
  chipColor?: string;
  chipBorderColor?: string;
  topGap?: boolean;
  renderChips: (squares: SquareNumbersForQuarter[], quarterNum: number) => ReactNode;
}

/** Returns payout amount for a specific row type and quarter */
function getRowPayoutForQuarter(
  rowType: RowType,
  quarterNum: number,
  board: Board,
  neighborPayoutPerQuarter: (qKey: string) => number,
): number {
  const payoutConfig = board.payoutConfig;
  if (!payoutConfig) return 0;
  const key = getNumberSetKeyForQuarter(quarterNum, board.quarters, board.numberMode || 'single');
  const fallback = `Q${quarterNum}`;
  const lookup = (map?: Record<string, number>) => map?.[key] ?? map?.[fallback] ?? 0;
  switch (rowType) {
    case 'rightWay':
      return lookup(payoutConfig.quarterPayouts);
    case 'wrongWay':
      return lookup(payoutConfig.quarterWrongWayPayouts);
    case 'plusNRightWay':
      return lookup(payoutConfig.quarterPlusNPayouts);
    case 'plusNWrongWay':
      return lookup(payoutConfig.quarterPlusNWrongWayPayouts);
    case 'neighborsRightWay':
    case 'neighborsWrongWay':
      return neighborPayoutPerQuarter(key || fallback);
    default:
      return 0;
  }
}

/** Sub-row showing per-quarter payouts under a row's chips (single-number mode) */
const QuarterPayoutSubRow = memo<{
  rowType: string;
  quarterLabels: Array<{ quarter: number; shortLabel: string }>;
  getPayoutForQuarter: (quarterNum: number) => number;
}>(({ rowType, quarterLabels, getPayoutForQuarter }) => (
  <Box
    data-testid={`my-squares-quarter-payouts-${rowType}`}
    sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.25 }}
  >
    {quarterLabels.map(({ quarter, shortLabel }) => {
      const amount = getPayoutForQuarter(quarter);
      return (
        <Typography key={quarter} variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          {shortLabel}{' '}
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            ${amount.toFixed(0)}
          </Box>
        </Typography>
      );
    })}
  </Box>
));
QuarterPayoutSubRow.displayName = 'QuarterPayoutSubRow';

export const MySquaresPanel = memo<MySquaresPanelProps>(({
  board,
  grid,
  hits,
  currentUserId,
  summary,
  onHighlight,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedQuarter, setSelectedQuarter] = useState<number | 'all'>('all');

  // Get user's squares from the grid
  const userSquares = useMemo(() => {
    return grid.flat().filter(s => s?.ownerId === currentUserId);
  }, [grid, currentUserId]);

  // Quarter labels for this board's sport
  const otEnabled = board.payoutConfig?.overtimeConfig?.enabled ?? false;
  const quarterLabels = useMemo(() => getQuarterLabels(board.quarters || 4, otEnabled), [board.quarters, otEnabled]);

  // Determine if multi-number mode (numbers differ per quarter)
  const isMultiNumber = board.numberMode === 'half' || board.numberMode === 'quarter';

  // Check if neighbors are configured
  const hasNeighborConfig = useMemo(() => {
    const nc = board.payoutConfig?.neighborPayouts;
    const bnc = board.payoutConfig?.boxNeighborPayouts;
    return (nc?.enabled === true) || (bnc?.enabled === true);
  }, [board.payoutConfig]);

  // Get which quarters already have hits recorded
  const completedQuarters = useMemo(() => {
    const set = new Set<number>();
    hits.forEach(h => {
      if (h.quarter != null && !h.isWrongWay && !h.isPlusN) {
        set.add(h.quarter);
      }
    });
    return set;
  }, [hits]);

  // Build a map of winning positions from hits for chip highlighting
  // Key includes quarter so highlights are quarter-specific: `${row}-${col}-${rowType}-${quarter}`
  const winningPositions = useMemo(() => {
    const map = new Map<string, { payoutAmount: number; quarter: number }>();
    const userSquareKeys = new Set(userSquares.map(s => `${s.row}-${s.col}`));

    for (const hit of hits) {
      if (hit.quarter == null) continue;

      // Determine the row type for this hit
      let rowType: RowType;
      if (hit.isPlusN && hit.isWrongWay) {
        rowType = 'plusNWrongWay';
      } else if (hit.isPlusN) {
        rowType = 'plusNRightWay';
      } else if (hit.isWrongWay) {
        rowType = 'wrongWay';
      } else {
        rowType = 'rightWay';
      }

      // Check if this hit's winning square belongs to the current user
      const hitKey = `${hit.winningRow}-${hit.winningCol}`;
      if (userSquareKeys.has(hitKey)) {
        const mapKey = `${hit.winningRow}-${hit.winningCol}-${rowType}-${hit.quarter}`;
        const existing = map.get(mapKey);
        map.set(mapKey, {
          payoutAmount: (existing?.payoutAmount ?? 0) + hit.payoutAmount,
          quarter: hit.quarter,
        });
      }

      // Check neighbor payouts for the user's squares
      if (hit.neighborPayouts) {
        const neighborRowType: RowType = hit.isWrongWay ? 'neighborsWrongWay' : 'neighborsRightWay';
        for (const np of hit.neighborPayouts) {
          const npKey = `${np.row}-${np.col}`;
          if (userSquareKeys.has(npKey)) {
            const mapKey = `${np.row}-${np.col}-${neighborRowType}-${hit.quarter}`;
            const existing = map.get(mapKey);
            map.set(mapKey, {
              payoutAmount: (existing?.payoutAmount ?? 0) + np.amount,
              quarter: hit.quarter,
            });
          }
        }
      }
    }

    return map;
  }, [hits, userSquares]);

  // Total winnings from backend ledger summary (same source as Players tab)
  const totalWinnings = useMemo(() => {
    const userBalance = (summary?.balances || []).find(b => b.userId === currentUserId);
    return userBalance?.totalEarned || 0;
  }, [summary, currentUserId]);

  // Compute square numbers for a given quarter
  const getSquareNumbersForQuarter = useCallback((quarterNum: number): SquareNumbersForQuarter[] => {
    if (!board.numbersRevealed) {
      return userSquares.map(sq => ({ square: sq, homeNumber: -1, awayNumber: -1 }));
    }

    if (!isMultiNumber) {
      return userSquares.map(sq => ({
        square: sq,
        homeNumber: sq.homeNumber,
        awayNumber: sq.awayNumber,
      }));
    }

    const { homeNumbers, awayNumbers } = getNumbersForQuarter(board, quarterNum);
    return userSquares.map(sq => {
      const homeNum = board.homeOnTop ? homeNumbers[sq.col] : homeNumbers[sq.row];
      const awayNum = board.homeOnTop ? awayNumbers[sq.row] : awayNumbers[sq.col];
      return {
        square: sq,
        homeNumber: homeNum ?? -1,
        awayNumber: awayNum ?? -1,
      };
    });
  }, [board, userSquares, isMultiNumber]);

  // Compute neighbor info for a square in a given quarter
  const getNeighborInfo = useCallback((sq: SquareWithNumbers, quarterNum: number): NeighborInfo => {
    const nc = board.payoutConfig?.neighborPayouts;
    const bnc = board.payoutConfig?.boxNeighborPayouts;
    const quarterKey = getNumberSetKeyForQuarter(quarterNum, board.quarters, board.numberMode || 'single');

    let count = 0;
    let amount = 0;

    if (nc?.enabled) {
      let directions: NeighborDirection[];
      if (nc.directions && nc.directions.length > 0) {
        directions = nc.directions;
      } else if (nc.type) {
        directions = nc.type === 'box' ? ALL_NEIGHBOR_DIRECTIONS : CARDINAL_DIRECTIONS;
      } else {
        directions = CARDINAL_DIRECTIONS;
      }
      count = directions.length;
      if (nc.usePerQuarterAmounts && nc.quarterAmounts) {
        amount = nc.quarterAmounts[quarterKey] ?? nc.globalAmount ?? 0;
      } else {
        amount = nc.globalAmount ?? 0;
      }
    } else if (bnc?.enabled && bnc.boxSize) {
      const boxNeighbors = getBoxNeighborPositions(sq.row, sq.col, bnc.boxSize);
      count = boxNeighbors.length;
      if (bnc.usePerQuarterAmounts && bnc.quarterAmounts) {
        amount = bnc.quarterAmounts[quarterKey] ?? bnc.globalAmount ?? 0;
      } else {
        amount = bnc.globalAmount ?? 0;
      }
    }

    const groupedWith: Array<{ row: number; col: number }> = [];
    const userSquareKeys = new Set(userSquares.map(s => `${s.row}-${s.col}`));

    if (nc?.enabled) {
      let directions: NeighborDirection[];
      if (nc.directions && nc.directions.length > 0) {
        directions = nc.directions;
      } else if (nc.type) {
        directions = nc.type === 'box' ? ALL_NEIGHBOR_DIRECTIONS : CARDINAL_DIRECTIONS;
      } else {
        directions = CARDINAL_DIRECTIONS;
      }
      const neighbors = getNeighborPositions(sq.row, sq.col, directions);
      for (const n of neighbors) {
        const key = `${n.row}-${n.col}`;
        if (userSquareKeys.has(key) && !(n.row === sq.row && n.col === sq.col)) {
          groupedWith.push({ row: n.row, col: n.col });
        }
      }
    } else if (bnc?.enabled && bnc.boxSize) {
      const boxNeighbors = getBoxNeighborPositions(sq.row, sq.col, bnc.boxSize);
      for (const n of boxNeighbors) {
        const key = `${n.row}-${n.col}`;
        if (userSquareKeys.has(key)) {
          groupedWith.push({ row: n.row, col: n.col });
        }
      }
    }

    return { count, amount, total: count * amount, groupedWith };
  }, [board, userSquares]);

  // Get unique neighbor cell number pairs for a set of user squares in a given quarter
  const getNeighborNumberPairs = useCallback((squares: SquareNumbersForQuarter[], quarterNum: number) => {
    const nc = board.payoutConfig?.neighborPayouts;
    const bnc = board.payoutConfig?.boxNeighborPayouts;

    const quarterNumbers = isMultiNumber ? getNumbersForQuarter(board, quarterNum) : null;

    const pairMap = new Map<string, { awayNumber: number; homeNumber: number; squareCount: number }>();

    for (const { square } of squares) {
      let positions: Array<{ row: number; col: number }> = [];
      if (nc?.enabled) {
        let directions: NeighborDirection[];
        if (nc.directions && nc.directions.length > 0) directions = nc.directions;
        else if (nc.type) directions = nc.type === 'box' ? ALL_NEIGHBOR_DIRECTIONS : CARDINAL_DIRECTIONS;
        else directions = CARDINAL_DIRECTIONS;
        positions = getNeighborPositions(square.row, square.col, directions);
      } else if (bnc?.enabled && bnc.boxSize) {
        positions = getBoxNeighborPositions(square.row, square.col, bnc.boxSize);
      }

      for (const pos of positions) {
        const cell = grid[pos.row]?.[pos.col];
        if (!cell) continue;

        let away: number, home: number;
        if (!board.numbersRevealed) {
          away = -1; home = -1;
        } else if (quarterNumbers) {
          home = board.homeOnTop ? quarterNumbers.homeNumbers[pos.col] : quarterNumbers.homeNumbers[pos.row];
          away = board.homeOnTop ? quarterNumbers.awayNumbers[pos.row] : quarterNumbers.awayNumbers[pos.col];
        } else {
          away = cell.awayNumber;
          home = cell.homeNumber;
        }

        const key = `${away}-${home}`;
        const existing = pairMap.get(key);
        if (existing) {
          existing.squareCount++;
        } else {
          pairMap.set(key, { awayNumber: away, homeNumber: home, squareCount: 1 });
        }
      }
    }

    return Array.from(pairMap.values());
  }, [board, grid, isMultiNumber]);

  // Compute payout summary
  const payoutSummary = useMemo(() => {
    const quarterPayouts = board.payoutConfig?.quarterPayouts || {};
    let totalMain = 0;
    let totalNeighbors = 0;

    for (const { quarter } of quarterLabels) {
      const key = getNumberSetKeyForQuarter(quarter, board.quarters, board.numberMode || 'single');
      const payoutKey = key || `Q${quarter}`;
      const qPayout = quarterPayouts[payoutKey] ?? quarterPayouts[`Q${quarter}`] ?? 0;
      totalMain += qPayout;

      if (hasNeighborConfig) {
        for (const sq of userSquares) {
          const info = getNeighborInfo(sq, quarter);
          totalNeighbors += info.total;
        }
      }
    }

    return { totalMain, totalNeighbors, quarterPayouts };
  }, [board, quarterLabels, userSquares, hasNeighborConfig, getNeighborInfo]);

  // Stable key for user square positions — only changes when squares change ownership, not on every SSE update
  const userSquarePositionKey = useMemo(() => {
    return userSquares.map(sq => `${sq.row}-${sq.col}`).sort().join(',');
  }, [userSquares]);

  // Handle highlighting when expanded state or quarter selection changes
  useEffect(() => {
    if (expanded && userSquarePositionKey) {
      const cells = userSquarePositionKey.split(',').map(key => {
        const [row, col] = key.split('-').map(Number);
        return { row, col };
      });
      onHighlight({
        cells,
        quarter: selectedQuarter === 'all' ? undefined : selectedQuarter,
      });
    } else if (!expanded) {
      onHighlight(null);
    }
  }, [expanded, selectedQuarter, userSquarePositionKey, onHighlight]);

  // Clean up highlighting on unmount
  useEffect(() => {
    return () => {
      onHighlight(null);
    };
  }, [onHighlight]);

  const handleToggleExpand = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const handleQuarterChange = useCallback((_: React.MouseEvent<HTMLElement>, value: number | 'all' | null) => {
    if (value !== null) {
      setSelectedQuarter(value);
    }
  }, []);

  // Don't render if user has no squares
  if (userSquares.length === 0) {
    return null;
  }


  const formatNumber = (num: number) => {
    if (!board.numbersRevealed || num === -1) return '?';
    return String(num);
  };

  // Determine which quarters to show based on filter
  const visibleQuarters = selectedQuarter !== 'all'
    ? quarterLabels.filter(q => q.quarter === selectedQuarter)
    : quarterLabels;

  const chipSx = { fontSize: '0.75rem', height: 24, '& .MuiChip-label': { px: 1 } };
  const winnerChipSx = {
    background: 'linear-gradient(135deg, #fff9c4 0%, #ffecb3 50%, #fff9c4 100%)',
    border: '2px solid',
    borderColor: '#ffc107',
    color: '#6d4c00',
    fontWeight: 700,
    boxShadow: '0 0 6px rgba(255, 193, 7, 0.3)',
  };

  // Quarter-aware winner check: in multi-number mode check specific quarter,
  // in single-number mode check if ANY quarter has a win for this position
  const isWinnerForQuarter = (row: number, col: number, rowType: RowType, quarterNum: number): boolean => {
    if (isMultiNumber) {
      return winningPositions.has(`${row}-${col}-${rowType}-${quarterNum}`);
    }
    // Single-number mode: check all quarters
    for (const { quarter } of quarterLabels) {
      if (winningPositions.has(`${row}-${col}-${rowType}-${quarter}`)) return true;
    }
    return false;
  };

  const labelSx = { textAlign: 'right' as const, fontWeight: 600, color: 'text.secondary', fontSize: '0.7rem', whiteSpace: 'nowrap' };
  const reverseEnabled = board.payoutConfig?.reverseEnabled === true ||
    board.payoutConfig?.wrongWayEnabled === true ||
    (board.payoutConfig?.quarterWrongWayPayouts != null &&
      Object.values(board.payoutConfig.quarterWrongWayPayouts).some(v => v > 0));

  const plusNOffset = board.payoutConfig?.plusNOffset ?? 5;
  const hasPlusN = board.payoutConfig?.quarterPlusNPayouts != null &&
    Object.values(board.payoutConfig.quarterPlusNPayouts).some(v => v > 0);

  const formatPlusN = (num: number) => {
    if (!board.numbersRevealed || num === -1) return '?';
    return String((num + plusNOffset) % 10);
  };

  // Neighbor payout per quarter (total across all user squares for one quarter)
  const neighborPayoutPerQuarter = (quarterKey: string): number => {
    const nc = board.payoutConfig?.neighborPayouts;
    const bnc = board.payoutConfig?.boxNeighborPayouts;
    if (nc?.enabled) {
      if (nc.usePerQuarterAmounts && nc.quarterAmounts) {
        return nc.quarterAmounts[quarterKey] ?? nc.globalAmount ?? 0;
      }
      return nc.globalAmount ?? 0;
    }
    if (bnc?.enabled) {
      if (bnc.usePerQuarterAmounts && bnc.quarterAmounts) {
        return bnc.quarterAmounts[quarterKey] ?? bnc.globalAmount ?? 0;
      }
      return bnc.globalAmount ?? 0;
    }
    return 0;
  };

  // Build row configs — DRY approach to avoid 6 conditional blocks
  const rowConfigs: RowConfig[] = [
    {
      type: 'rightWay',
      label: 'Right Way',
      visible: true,
      renderChips: (squares, quarterNum) => squares.map(({ square, awayNumber, homeNumber }) => {
        const isWinner = isWinnerForQuarter(square.row, square.col, 'rightWay', quarterNum);
        return (
          <Chip
            key={`rw-${square.row}-${square.col}`}
            label={`${formatNumber(awayNumber)} - ${formatNumber(homeNumber)}`}
            size="small"
            variant="outlined"
            data-testid={isWinner ? `winner-chip-rightWay-${square.row}-${square.col}` : undefined}
            sx={{ ...chipSx, ...(isWinner ? winnerChipSx : {}) }}
          />
        );
      }),
    },
    {
      type: 'wrongWay',
      label: 'Wrong Way',
      visible: reverseEnabled,
      renderChips: (squares, quarterNum) => squares.map(({ square, awayNumber, homeNumber }) => {
        const isWinner = isWinnerForQuarter(square.row, square.col, 'wrongWay', quarterNum);
        return (
          <Chip
            key={`ww-${square.row}-${square.col}`}
            label={`${formatNumber(homeNumber)} - ${formatNumber(awayNumber)}`}
            size="small"
            variant="outlined"
            data-testid={isWinner ? `winner-chip-wrongWay-${square.row}-${square.col}` : undefined}
            sx={{ ...chipSx, ...(isWinner ? winnerChipSx : {}) }}
          />
        );
      }),
    },
    {
      type: 'plusNRightWay',
      label: `+${plusNOffset} RW`,
      visible: hasPlusN,
      chipColor: 'info.main',
      chipBorderColor: 'info.main',
      renderChips: (squares, quarterNum) => squares.map(({ square, awayNumber, homeNumber }) => {
        const isWinner = isWinnerForQuarter(square.row, square.col, 'plusNRightWay', quarterNum);
        return (
          <Chip
            key={`pnrw-${square.row}-${square.col}`}
            label={`${formatPlusN(awayNumber)} - ${formatPlusN(homeNumber)}`}
            size="small"
            variant="outlined"
            data-testid={isWinner ? `winner-chip-plusNRightWay-${square.row}-${square.col}` : undefined}
            sx={{ ...chipSx, color: 'info.main', borderColor: 'info.main', ...(isWinner ? winnerChipSx : {}) }}
          />
        );
      }),
    },
    {
      type: 'plusNWrongWay',
      label: `+${plusNOffset} WW`,
      visible: hasPlusN && reverseEnabled,
      chipColor: 'info.main',
      chipBorderColor: 'info.main',
      renderChips: (squares, quarterNum) => squares.map(({ square, awayNumber, homeNumber }) => {
        const isWinner = isWinnerForQuarter(square.row, square.col, 'plusNWrongWay', quarterNum);
        return (
          <Chip
            key={`pnww-${square.row}-${square.col}`}
            label={`${formatPlusN(homeNumber)} - ${formatPlusN(awayNumber)}`}
            size="small"
            variant="outlined"
            data-testid={isWinner ? `winner-chip-plusNWrongWay-${square.row}-${square.col}` : undefined}
            sx={{ ...chipSx, color: 'info.main', borderColor: 'info.main', ...(isWinner ? winnerChipSx : {}) }}
          />
        );
      }),
    },
    {
      type: 'neighborsRightWay',
      label: 'Neighbors RW',
      visible: hasNeighborConfig,
      topGap: true,
      chipColor: 'success.main',
      chipBorderColor: 'success.main',
      renderChips: (squares, quarterNum) => getNeighborNumberPairs(squares, quarterNum).map(({ awayNumber, homeNumber, squareCount }) => (
        <Tooltip
          key={`nrw-${awayNumber}-${homeNumber}`}
          title={squareCount > 1 ? `${squareCount} of your squares neighbor this` : ''}
          arrow
          placement="top"
        >
          <Chip
            label={`${formatNumber(awayNumber)} - ${formatNumber(homeNumber)}`}
            size="small"
            variant="outlined"
            sx={{ ...chipSx, color: 'success.main', borderColor: 'success.main' }}
          />
        </Tooltip>
      )),
    },
    {
      type: 'neighborsWrongWay',
      label: 'Neighbors WW',
      visible: hasNeighborConfig && reverseEnabled,
      chipColor: 'success.main',
      chipBorderColor: 'success.main',
      renderChips: (squares, quarterNum) => getNeighborNumberPairs(squares, quarterNum).map(({ awayNumber, homeNumber, squareCount }) => (
        <Tooltip
          key={`nww-${awayNumber}-${homeNumber}`}
          title={squareCount > 1 ? `${squareCount} of your squares neighbor this` : ''}
          arrow
          placement="top"
        >
          <Chip
            label={`${formatNumber(homeNumber)} - ${formatNumber(awayNumber)}`}
            size="small"
            variant="outlined"
            sx={{ ...chipSx, color: 'success.main', borderColor: 'success.main' }}
          />
        </Tooltip>
      )),
    },
  ];

  const visibleRows = rowConfigs.filter(r => r.visible);

  const renderSquaresGrid = (squares: SquareNumbersForQuarter[], quarterNum: number) => {
    if (isMultiNumber) {
      // Multi-number: 3-column grid — label | payout | chips
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', columnGap: 1.5, rowGap: 0.5, alignItems: 'center' }}>
          {visibleRows.map(config => (
            <Box key={config.type} sx={{ display: 'contents' }}>
              <Typography variant="caption" sx={{ ...labelSx, ...(config.topGap ? { mt: 0.5 } : {}) }}>
                {config.label}
              </Typography>
              <Typography
                variant="caption"
                data-testid={`my-squares-payout-${config.type}`}
                sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.7rem', ...(config.topGap ? { mt: 0.5 } : {}) }}
              >
                ${getRowPayoutForQuarter(config.type, quarterNum, board, neighborPayoutPerQuarter).toFixed(0)}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ...(config.topGap ? { mt: 0.5 } : {}) }}>
                {config.renderChips(squares, quarterNum)}
              </Box>
            </Box>
          ))}
        </Box>
      );
    }

    // Single-number: 2-column grid — label | chips + sub-row
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.5, alignItems: 'center' }}>
        {visibleRows.map(config => (
          <Box key={config.type} sx={{ display: 'contents' }}>
            <Typography variant="caption" sx={{ ...labelSx, ...(config.topGap ? { mt: 0.5 } : {}), alignSelf: 'start', mt: config.topGap ? 0.5 : 0.25 }}>
              {config.label}
            </Typography>
            <Box sx={{ ...(config.topGap ? { mt: 0.5 } : {}) }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {config.renderChips(squares, quarterNum)}
              </Box>
              <QuarterPayoutSubRow
                rowType={config.type}
                quarterLabels={quarterLabels}
                getPayoutForQuarter={(q) => getRowPayoutForQuarter(config.type, q, board, neighborPayoutPerQuarter)}
              />
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Paper
      data-testid="my-squares-panel"
      sx={{
        mb: 3,
        border: '1px solid',
        borderColor: 'grey.200',
        overflow: 'hidden',
      }}
    >
      {/* Header row — title, count, controls (when expanded), expand button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          bgcolor: 'grey.50',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'grey.100' },
          flexWrap: 'wrap',
        }}
        onClick={handleToggleExpand}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            My Squares
          </Typography>
          <Chip
            label={userSquares.length}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          {totalWinnings > 0 && (
            <Chip
              data-testid="my-squares-winnings"
              icon={<TrophyIcon sx={{ fontSize: 16 }} />}
              label={`$${Math.round(totalWinnings)}`}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #fff9c4 0%, #ffecb3 50%, #fff9c4 100%)',
                border: '1px solid #ffc107',
                color: '#6d4c00',
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 26,
                '& .MuiChip-icon': { color: '#ffa000' },
                animation: 'winnerGlow 2s ease-in-out infinite',
                '@keyframes winnerGlow': {
                  '0%, 100%': { boxShadow: '0 0 4px rgba(255, 193, 7, 0.3)' },
                  '50%': { boxShadow: '0 0 10px rgba(255, 193, 7, 0.6)' },
                },
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <IconButton
            size="small"
            data-testid="my-squares-expand-button"
            onClick={(e) => { e.stopPropagation(); handleToggleExpand(); }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      {/* Expanded content — labeled rows + summary */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 1.5 }}>
          {/* Quarter toggle — inside container for better mobile fit */}
          {isMultiNumber && (
            <Box sx={{ overflow: 'auto', display: 'flex', alignItems: 'center', pt: 1, pb: 0.5 }}>
              <ToggleButtonGroup
                value={selectedQuarter}
                exclusive
                onChange={handleQuarterChange}
                size="small"
                data-testid="my-squares-quarter-toggle"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 1,
                    py: 0.25,
                    fontSize: '0.65rem',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <ToggleButton value="all">All</ToggleButton>
                {quarterLabels.map(({ quarter, shortLabel }) => (
                  <ToggleButton key={quarter} value={quarter}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {shortLabel}
                      {completedQuarters.has(quarter) && (
                        <CheckIcon sx={{ fontSize: 10, color: 'success.main' }} />
                      )}
                    </Box>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          )}
          <Box data-testid="my-squares-table" sx={{ pt: isMultiNumber ? 0.5 : 1 }}>
            {isMultiNumber ? (
              visibleQuarters.map(({ quarter, shortLabel }) => {
                const squareNumbers = getSquareNumbersForQuarter(quarter);
                return (
                  <Box key={quarter} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {shortLabel}
                      </Typography>
                      {completedQuarters.has(quarter) && (
                        <CheckIcon sx={{ fontSize: 12, color: 'success.main' }} />
                      )}
                    </Box>
                    {renderSquaresGrid(squareNumbers, quarter)}
                  </Box>
                );
              })
            ) : (
              renderSquaresGrid(
                getSquareNumbersForQuarter(1),
                selectedQuarter === 'all' ? 1 : selectedQuarter,
              )
            )}
          </Box>

          {/* Payout summary */}
          <Box
            data-testid="my-squares-summary"
            sx={{ mt: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Potential:{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                ${payoutSummary.totalMain.toFixed(0)}
              </Box>
            </Typography>

            {hasNeighborConfig && payoutSummary.totalNeighbors > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Neighbors:{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  ${payoutSummary.totalNeighbors.toFixed(0)}
                </Box>
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
});

MySquaresPanel.displayName = 'MySquaresPanel';

export default MySquaresPanel;
